import React, { useEffect, useId, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { CloudOff, Search, Sparkles, UserSearch, X } from 'lucide-react';
import Card from '../ui/Card.jsx';
import Skeleton from '../ui/Skeleton.jsx';
import EmptyState from '../ui/EmptyState.jsx';
import Button from '../ui/Button.jsx';
import { Input, inputActionClasses } from '../ui/Input.jsx';
import SkillBrowseRow, { HelperStack, describeSkill, skillShape } from './SkillBrowseRow.jsx';
import SkillDetailSheet from './SkillDetailSheet.jsx';
import { useSkillBrowse } from '../../hooks/useSkillBrowse';
import { trackBrowseEvent } from '../../utils/browseEvents';
import { matches } from '../../utils/skillSearchNormalize';

/**
 * Reiter „Skills“ der Startseite (Skills entdecken, PLAN § 2.1/2.2).
 *
 * Aufbau wie im Entwurf (docs/features/skills-entdecken/entwurf-v1.html):
 *   1. „Für dich“: Skills aus den eigenen Lernzielen, bei denen jemand helfen
 *      kann, als waagrechte Reihe (höchstens 6). Fehlt, wenn es keine gibt.
 *   2. Der Katalog der Schule, Kategorien als graue Versal-Überschriften, darunter
 *      je eine ruhige Liste. Reihenfolge genau wie vom Server geliefert
 *      (Kategorie-Reihenfolge der Schule, sortOrder), hier wird nichts sortiert.
 *      Skills ohne Personen liefert der Server gar nicht erst.
 *
 * Filter: Die Lupe sitzt ab Phase 3 in der Kopfzeile der Startseite. Dafür ist der
 * Zustand steuerbar (`query`/`onQueryChange`, `searchOpen`/`onSearchOpenChange`).
 * Ohne `searchOpen` bringt die Ansicht ihre eigene Lupe mit (Design-Katalog).
 * Gefiltert wird im Browser (utils/skillSearchNormalize: Umlaute, ß, Teilwort).
 *
 * CD: Nur Name und Köpfe, keine Zahlen und keine farbigen Punkte (die stehen im
 * Detailblatt). Lila nur im Fokusring, Gliederung über Abstand.
 */

const FOR_YOU_MAX = 6;

const SectionLabel = ({ children, id }) => (
  <h3 id={id} className="px-1 text-2xs font-semibold uppercase tracking-[0.08em] text-muted-foreground">
    {children}
  </h3>
);

SectionLabel.propTypes = { children: PropTypes.node, id: PropTypes.string };

const ForYouTile = ({ skill, onOpen }) => (
  <button
    type="button"
    onClick={() => onOpen(skill)}
    aria-label={describeSkill(skill)}
    className={[
      'flex w-[9.5rem] shrink-0 snap-start flex-col items-start gap-2 rounded-2xl border border-border bg-surface p-3 text-left',
      'touch-manipulation transition-colors duration-fast hover:bg-surface-raised',
      'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary',
    ].join(' ')}
  >
    <span className="block w-full truncate text-label font-semibold text-foreground" title={skill.name}>
      {skill.name}
    </span>
    <HelperStack preview={skill.preview} />
  </button>
);

ForYouTile.propTypes = { skill: skillShape.isRequired, onOpen: PropTypes.func.isRequired };

const LoadingState = () => (
  <div className="space-y-3" aria-busy="true">
    <span className="sr-only">Skills werden geladen…</span>
    <div className="mx-1 h-3 w-24 animate-pulse rounded bg-surface-raised" aria-hidden="true" />
    <Card variant="flat" className="divide-y divide-border px-1 py-1">
      {Array.from({ length: 4 }).map((_, index) => <Skeleton.Row key={index} />)}
    </Card>
  </div>
);

/** Die reine Ansicht: bekommt Daten, lädt nichts selbst. */
export const SkillBrowseContent = ({
  data,
  isLoading = false,
  error = null,
  onRetry,
  query: queryProp,
  onQueryChange,
  searchOpen: searchOpenProp,
  onSearchOpenChange,
  onSearchPeople,
  onEditProfile,
  peopleLoader,
  trackEvents = true,
  className = '',
}) => {
  const [ownQuery, setOwnQuery] = useState('');
  const [ownOpen, setOwnOpen] = useState(false);
  const [openSkill, setOpenSkill] = useState(null);
  const inputRef = useRef(null);
  const fieldId = useId();

  const queryControlled = queryProp !== undefined;
  const openControlled = searchOpenProp !== undefined;
  const query = queryControlled ? queryProp : ownQuery;
  const searchOpen = openControlled ? searchOpenProp : ownOpen;
  const term = searchOpen || queryControlled ? query.trim() : '';

  const setQuery = (value) => {
    if (!queryControlled) setOwnQuery(value);
    if (onQueryChange) onQueryChange(value);
  };
  const setSearchOpen = (value) => {
    if (!openControlled) setOwnOpen(value);
    if (onSearchOpenChange) onSearchOpenChange(value);
  };

  // Aufklappen = sofort tippen können (auch wenn die Lupe außerhalb sitzt)
  useEffect(() => {
    if (searchOpen && inputRef.current) inputRef.current.focus({ preventScroll: true });
  }, [searchOpen]);

  const categories = useMemo(
    () => (data?.categories || [])
      .map(category => ({ ...category, skills: (category.skills || []).filter(Boolean) }))
      .filter(category => category.skills.length > 0),
    [data],
  );

  const skillIndex = useMemo(() => {
    const index = new Map();
    categories.forEach(category => category.skills.forEach(skill => index.set(skill.id, { skill, category: category.name })));
    return index;
  }, [categories]);

  const forYou = useMemo(
    () => (data?.forYou || []).map(id => skillIndex.get(id)?.skill).filter(Boolean).slice(0, FOR_YOU_MAX),
    [data, skillIndex],
  );

  const filtered = useMemo(() => {
    if (!term) return categories;
    return categories
      .map(category => ({ ...category, skills: category.skills.filter(skill => matches(skill.name, term)) }))
      .filter(category => category.skills.length > 0);
  }, [categories, term]);

  const openDetail = (skill) => {
    setOpenSkill(skill);
    if (trackEvents) trackBrowseEvent('BROWSE_SKILL_OPENED', { skillId: skill.id });
  };

  const closeSearch = () => {
    setQuery('');
    setSearchOpen(false);
  };

  const searchPeopleButton = term && onSearchPeople ? (
    <Button variant="secondary" size="sm" onClick={() => onSearchPeople(term)} className="max-w-full">
      <UserSearch className="h-4 w-4 shrink-0" aria-hidden="true" />
      <span className="min-w-0 truncate">{`Personen mit „${term}“ suchen`}</span>
    </Button>
  ) : null;

  let body;
  if (isLoading && !data) {
    body = <LoadingState />;
  } else if (error && !data) {
    body = (
      <div role="status">
        <EmptyState
          size="sm"
          icon={CloudOff}
          title="Die Skills konnten gerade nicht geladen werden"
          text="Prüf kurz deine Verbindung und versuch es dann noch einmal."
          action={onRetry ? <Button variant="secondary" onClick={onRetry}>Noch mal versuchen</Button> : null}
        />
      </div>
    );
  } else if (categories.length === 0) {
    body = (
      <EmptyState
        size="sm"
        icon={Sparkles}
        title="Noch hat niemand Skills eingetragen"
        text="Trag ein, wobei du helfen kannst – dann finden dich andere hier."
        action={onEditProfile ? <Button onClick={onEditProfile}>Skills eintragen</Button> : null}
      />
    );
  } else if (filtered.length === 0) {
    body = (
      <div role="status">
        <EmptyState
          size="sm"
          icon={Search}
          title={<span className="break-words">{`Kein Skill passt zu „${term}“`}</span>}
          text="Vielleicht findest du jemanden über die Personensuche."
          action={searchPeopleButton}
        />
      </div>
    );
  } else {
    body = (
      <div className="space-y-6">
        {!term && forYou.length > 0 && (
          <section aria-labelledby={`${fieldId}-foryou`} className="space-y-2">
            <div>
              <SectionLabel id={`${fieldId}-foryou`}>Für dich</SectionLabel>
              <p className="px-1 text-meta text-muted-foreground">Aus deinen Lernzielen</p>
            </div>
            {/* Seitlich scrollend; der negative Rand lässt die Reihe bis an den
                Bildschirmrand laufen, ohne die Seite breiter zu machen. Er muss
                genau dem Seitenrand von MainScreen entsprechen (`px-3 sm:px-4`),
                sonst ragt die Reihe über den Bildschirm hinaus. */}
            <div className="-mx-3 overflow-x-auto px-3 pb-1 sm:-mx-4 sm:px-4 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
              <div className="flex snap-x gap-2">
                {forYou.map(skill => <ForYouTile key={skill.id} skill={skill} onOpen={openDetail} />)}
              </div>
            </div>
          </section>
        )}

        {filtered.map((category, index) => (
          <section key={category.name} aria-labelledby={`${fieldId}-cat-${index}`} className="space-y-2">
            <SectionLabel id={`${fieldId}-cat-${index}`}>{category.name}</SectionLabel>
            <Card variant="flat" className="divide-y divide-border overflow-hidden">
              {category.skills.map(skill => <SkillBrowseRow key={skill.id} skill={skill} onOpen={openDetail} />)}
            </Card>
          </section>
        ))}

        {searchPeopleButton && <div className="flex justify-center">{searchPeopleButton}</div>}
      </div>
    );
  }

  const canFilter = categories.length > 0;

  // Ohne Katalog (lädt, leer, Fehler) gibt es kein Filterfeld. Dann darf die
  // Lupe auch nicht „aufgeklappt“ melden (aria-expanded im Kopf): zurücksetzen.
  useEffect(() => {
    if (searchOpen && !canFilter) setSearchOpen(false);
  }, [searchOpen, canFilter]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className={`min-w-0 space-y-4 ${className}`}>
      {!openControlled && canFilter && !searchOpen && (
        <div className="flex justify-end">
          <button
            type="button"
            onClick={() => setSearchOpen(true)}
            aria-label="Skills filtern"
            className="inline-flex h-10 w-10 items-center justify-center rounded-full border border-border bg-surface text-foreground transition-colors duration-fast hover:bg-surface-raised focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
          >
            <Search className="h-[18px] w-[18px]" aria-hidden="true" />
          </button>
        </div>
      )}

      {searchOpen && canFilter && (
        <div role="search">
          <label htmlFor={fieldId} className="sr-only">Skills filtern</label>
          <Input
            ref={inputRef}
            id={fieldId}
            type="search"
            value={query}
            onChange={event => setQuery(event.target.value)}
            onKeyDown={(event) => { if (event.key === 'Escape') closeSearch(); }}
            placeholder="Skill suchen, z. B. Excel"
            leadingIcon={Search}
            autoComplete="off"
            // Eigenes Kreuz rechts; das blaue Kreuz des Browsers (type=search) wäre doppelt
            className="[&::-webkit-search-cancel-button]:appearance-none [&::-webkit-search-decoration]:appearance-none"
            trailingAction={(
              <button type="button" onClick={closeSearch} aria-label="Filter schließen" className={inputActionClasses}>
                <X className="h-4 w-4" aria-hidden="true" />
              </button>
            )}
          />
        </div>
      )}

      {body}

      <SkillDetailSheet
        open={Boolean(openSkill)}
        onClose={() => setOpenSkill(null)}
        skill={openSkill}
        category={openSkill ? skillIndex.get(openSkill.id)?.category : undefined}
        loader={peopleLoader}
        trackEvents={trackEvents}
      />
    </div>
  );
};

const sharedPropTypes = {
  query: PropTypes.string,
  onQueryChange: PropTypes.func,
  searchOpen: PropTypes.bool,
  onSearchOpenChange: PropTypes.func,
  /** „Personen mit „…“ suchen“: Übergang in die Personensuche */
  onSearchPeople: PropTypes.func,
  /** EmptyState „Noch hat niemand Skills eingetragen“ → eigenes Profil */
  onEditProfile: PropTypes.func,
  /** loader(skillId, role, cursor) für das Detailblatt; Standard: API */
  peopleLoader: PropTypes.func,
  className: PropTypes.string,
};

SkillBrowseContent.propTypes = {
  ...sharedPropTypes,
  data: PropTypes.shape({
    categories: PropTypes.arrayOf(PropTypes.shape({
      name: PropTypes.string.isRequired,
      skills: PropTypes.arrayOf(skillShape),
    })),
    forYou: PropTypes.arrayOf(PropTypes.string),
  }),
  isLoading: PropTypes.bool,
  error: PropTypes.any,
  onRetry: PropTypes.func,
  trackEvents: PropTypes.bool,
};

/** Mit Server: lädt erst, wenn `enabled`, und meldet die Öffnung des Reiters. */
const SkillBrowseLive = ({ enabled = true, ...rest }) => {
  const { data, isLoading, error, refresh } = useSkillBrowse({ enabled });
  const reportedRef = useRef(false);

  // Einmal je Öffnung des Reiters; React StrictMode hängt Effekte doppelt ein,
  // deshalb setzt nur das Schließen (`enabled` falsch) die Marke zurück.
  useEffect(() => {
    if (!enabled) {
      reportedRef.current = false;
      return;
    }
    if (reportedRef.current) return;
    reportedRef.current = true;
    trackBrowseEvent('BROWSE_TAB_OPENED');
  }, [enabled]);

  if (!enabled) return null;
  return <SkillBrowseContent data={data} isLoading={isLoading} error={error} onRetry={refresh} {...rest} />;
};

SkillBrowseLive.propTypes = { ...sharedPropTypes, enabled: PropTypes.bool };

/**
 * - initialData: feste Daten statt API (Design-Katalog). Dann kein Abruf und
 *   keine Ereignisse.
 * - enabled: Reiter sichtbar (lädt erst dann).
 */
const SkillBrowseView = ({ initialData, ...rest }) => (initialData
  ? <SkillBrowseContent data={initialData} trackEvents={false} {...rest} />
  : <SkillBrowseLive {...rest} />);

SkillBrowseView.propTypes = {
  ...sharedPropTypes,
  initialData: SkillBrowseContent.propTypes.data,
  enabled: PropTypes.bool,
};

export default SkillBrowseView;
